// src/components/group/SettingsSection.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function SettingsSection({ 
  group, 
  groupId, 
  token, 
  apiUrl, 
  advancedSettings, 
  setAdvancedSettings,
  onUpdate,
  onDelete,
  isOpen,
  onToggle
}) {
  // ✅ FIXAT: Lade till i18n för språkstöd
  const { t, i18n } = useTranslation();
  const [testingEmails, setTestingEmails] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [validationError, setValidationError] = useState('');

  const isRecurring = group.frequency === 'weekly' || group.frequency === 'biweekly';
  const isOneTime = group.frequency === 'one-time';

  // ⭐ SMART VALIDERING: Beräkna min/max värden baserat på frequency
  const getInvitationConstraints = () => {
    if (isRecurring) {
      return { min: 3, label: t('settings.minDaysRecurring', { days: 3 }) };
    }
    if (isOneTime) {
      return { min: 1, label: t('settings.minDaysOneTime', { days: 1 }) };
    }
    return { min: 1, label: '' };
  };

  const getReminderConstraints = () => {
    const invitationDays = parseInt(advancedSettings.invitationAdvance) || 1;
    
    if (isRecurring) {
      return { 
        min: 1, 
        max: invitationDays - 1,
        disabled: false,
        label: t('settings.minDaysRecurring', { days: 1 })
      };
    }
    
    if (isOneTime) {
      // Om inbjudan är 1-2 dagar före → ingen plats för påminnelse
      if (invitationDays <= 2) {
        return { 
          min: 1, 
          max: 0,
          disabled: true,
          label: t('settings.noRoomForReminder')
        };
      }
      // Om inbjudan är 3+ dagar → påminnelse kan vara 1 dag före
      return { 
        min: 1, 
        max: invitationDays - 1,
        disabled: false,
        label: t('settings.reminderMustBeBefore', { days: invitationDays - 1 })
      };
    }
    
    return { min: 1, max: invitationDays - 1, disabled: false, label: '' };
  };

  const invitationConstraints = getInvitationConstraints();
  const reminderConstraints = getReminderConstraints();

  // ⭐ AUTO-ADJUST: När invitationAdvance ändras, justera reminderAdvance om den blir ogiltig
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const invitationDays = parseInt(advancedSettings.invitationAdvance) || 1;
    const reminderDays = parseInt(advancedSettings.reminderAdvance) || 1;
    
    // För one-time: Om inbjudan <= 2 dagar, sätt påminnelse till 0 (disabled)
    if (isOneTime && invitationDays <= 2 && reminderDays > 0) {
      setAdvancedSettings(prev => ({
        ...prev,
        reminderAdvance: 0
      }));
    }
    
    // Om påminnelse är >= inbjudan, justera ner
    if (reminderDays >= invitationDays) {
      const maxReminder = Math.max(1, invitationDays - 1);
      setAdvancedSettings(prev => ({
        ...prev,
        reminderAdvance: maxReminder
      }));
    }
  }, [advancedSettings.invitationAdvance, isOneTime]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // ⭐ VALIDERING före sparning
    const invitationDays = parseInt(advancedSettings.invitationAdvance);
    const reminderDays = parseInt(advancedSettings.reminderAdvance);
    
    // Validera invitation
    if (invitationDays < invitationConstraints.min) {
      setValidationError(t('settings.invitationTooShort', { min: invitationConstraints.min }));
      return;
    }
    
    // Validera reminder
    if (!reminderConstraints.disabled) {
      if (reminderDays < reminderConstraints.min) {
        setValidationError(t('settings.reminderTooShort', { min: reminderConstraints.min }));
        return;
      }
      if (reminderDays >= invitationDays) {
        setValidationError(t('settings.reminderMustBeBeforeInvitation'));
        return;
      }
    }
    
    setValidationError('');
    onUpdate(advancedSettings);
  };

  const handleTestEmails = async () => {
    if (group.participants.length === 0) {
      alert(t('settings.addParticipantFirst'));
      return;
    }

    if (!window.confirm(t('settings.confirmTestEmails'))) return;

    setTestingEmails(true);
    setTestEmailResult(null);

    try {
      // ✅ FIXAT: Skicka med språk i URL
      const response = await fetch(`${apiUrl}/api/groups/${groupId}/test-emails?lang=${i18n.language}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        setTestEmailResult({
          success: true,
          message: data.message,
          recipient: data.recipient,
          emails: data.emails
        });
      } else {
        setTestEmailResult({
          success: false,
          message: data.message || t('settings.somethingWentWrong')
        });
      }
    } catch (error) {
      console.error('Test email error:', error);
      setTestEmailResult({
        success: false,
        message: t('settings.couldNotSendTestEmail')
      });
    } finally {
      setTestingEmails(false);
    }
  };

  const timezoneOptions = [
    { group: t('settings.timezones.europe'), options: [
      { value: 'Europe/Stockholm', label: 'Stockholm (UTC+1/+2)' },
      { value: 'Europe/London', label: 'London (UTC+0/+1)' },
      { value: 'Europe/Paris', label: 'Paris (UTC+1/+2)' },
      { value: 'Europe/Berlin', label: 'Berlin (UTC+1/+2)' }
    ]},
    { group: t('settings.timezones.america'), options: [
      { value: 'America/New_York', label: 'New York (UTC-5/-4)' },
      { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)' }
    ]},
    { group: t('settings.timezones.asia'), options: [
      { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
      { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' }
    ]}
  ];

  return (
    <section className="settings-section">
      <div className="section-header" onClick={onToggle}>
        <h2>{t('settings.title')}</h2>
        <span className="toggle-icon">{isOpen ? '\u25BC' : '\u25B6'}</span>
      </div>
      {isOpen && (
        <div>
          <form onSubmit={handleSubmit} className="settings-form">
            {/* Timezone */}
            <div className="form-row">
              <div className="form-group">
                <label>{t('settings.timezone')}</label>
                <select
                  value={advancedSettings.timezone}
                  onChange={(e) => setAdvancedSettings({...advancedSettings, timezone: e.target.value})}
                >
                  {timezoneOptions.map(region => (
                    <optgroup key={region.group} label={region.group}>
                      {region.options.map(tz => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <small>{t('settings.timezoneDesc')}</small>
              </div>
            </div>

            {/* Email timings med smart validering */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  {t('settings.invitationAdvance')}
                  {isRecurring && <span className="badge-recurring">{t('settings.recurring')}</span>}
                  {isOneTime && <span className="badge-onetime">{t('settings.oneTime')}</span>}
                </label>
                <input
                  type="number"
                  min={invitationConstraints.min}
                  value={advancedSettings.invitationAdvance}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || invitationConstraints.min;
                    setAdvancedSettings({
                      ...advancedSettings, 
                      invitationAdvance: Math.max(value, invitationConstraints.min)
                    });
                  }}
                />
                <small>
                  {t('settings.invitationAdvanceDesc')}
                  <br />
                  <strong style={{ color: isRecurring ? '#2196F3' : '#FF9800' }}>
                    {invitationConstraints.label}
                  </strong>
                </small>
              </div>

              <div className="form-group">
                <label>
                  {t('settings.reminderAdvance')}
                  {reminderConstraints.disabled && (
                    <span className="badge-disabled">{t('settings.disabled')}</span>
                  )}
                </label>
                <input
                  type="number"
                  min={reminderConstraints.min}
                  max={reminderConstraints.max}
                  value={advancedSettings.reminderAdvance}
                  disabled={reminderConstraints.disabled}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || reminderConstraints.min;
                    setAdvancedSettings({
                      ...advancedSettings, 
                      reminderAdvance: Math.min(
                        Math.max(value, reminderConstraints.min),
                        reminderConstraints.max
                      )
                    });
                  }}
                  style={{
                    backgroundColor: reminderConstraints.disabled ? '#f0f0f0' : 'white',
                    cursor: reminderConstraints.disabled ? 'not-allowed' : 'text'
                  }}
                />
                <small>
                  {reminderConstraints.disabled ? (
                    <span style={{ color: '#FF5722' }}>
                      {reminderConstraints.label}
                    </span>
                  ) : (
                    <>
                      {t('settings.reminderAdvanceDesc')}
                      <br />
                      <strong style={{ color: '#4CAF50' }}>
                        {reminderConstraints.label}
                      </strong>
                    </>
                  )}
                </small>
              </div>

              <div className="form-group">
                <label>{t('settings.decisionTime')}</label>
                <input
                  type="time"
                  value={advancedSettings.decisionTime}
                  onChange={(e) => setAdvancedSettings({...advancedSettings, decisionTime: e.target.value})}
                />
                <small>{t('settings.decisionTimeDesc')}</small>
              </div>
            </div>

            {/* Validering error */}
            {validationError && (
              <div style={{
                padding: '12px',
                backgroundColor: '#ffebee',
                border: '1px solid #ef5350',
                borderRadius: '5px',
                color: '#c62828',
                marginBottom: '15px'
              }}>
                {'\u26A0\uFE0F'} {validationError}
              </div>
            )}

            {/* Info box om aktuella regler */}
            <div style={{
              padding: '15px',
              backgroundColor: isRecurring ? '#e3f2fd' : '#fff3e0',
              border: `2px solid ${isRecurring ? '#2196F3' : '#FF9800'}`,
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 10px 0', color: isRecurring ? '#1976D2' : '#F57C00' }}>
                {'\u{1F4CB}'} {isRecurring ? t('settings.recurringRules') : t('settings.oneTimeRules')}
              </h4>
              {isRecurring ? (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>{t('settings.ruleRecurringInvitation')}</li>
                  <li>{t('settings.ruleRecurringReminder')}</li>
                  <li>{t('settings.ruleReminderBeforeInvitation')}</li>
                </ul>
              ) : (
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  <li>{t('settings.ruleOneTimeInvitation')}</li>
                  <li>{t('settings.ruleOneTimeReminder1')}</li>
                  <li>{t('settings.ruleOneTimeReminder2')}</li>
                </ul>
              )}
            </div>

            <button type="submit" className="btn-save">
              {t('settings.saveChanges')}
            </button>
          </form>

          {/* Test emails */}
          <div className="test-email-section">
            <h3>{t('settings.testEmailTitle')}</h3>
            <p>{t('settings.testEmailDesc')}</p>
            
            <button
              onClick={handleTestEmails}
              disabled={testingEmails || group.participants?.length === 0}
              className="btn-test"
            >
              {testingEmails ? t('settings.sending') : t('settings.testEmailButton')}
            </button>

            {testEmailResult && (
              <div className={`test-result ${testEmailResult.success ? 'success' : 'error'}`}>
                <strong>
                  {testEmailResult.success ? '\u2705 ' + t('settings.success') : '\u274C ' + t('settings.error')}
                </strong>
                <p>{testEmailResult.message}</p>
                {testEmailResult.success && testEmailResult.emails && (
                  <ul>
                    {testEmailResult.emails.map((email, index) => (
                      <li key={index}>{email}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Delete group */}
          <div className="danger-zone">
            <h3>{t('settings.dangerZone')}</h3>
            <p>{t('settings.cannotBeUndone')}</p>
            <button onClick={onDelete} className="btn-delete">
              {t('settings.deleteGroup')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default SettingsSection;
